import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../../components/ui/Card';
import { TextField } from '../../../components/ui/Field';
import { DataTable, ErrorState, TableSkeleton } from '../../../components/ui/Page';
import { useToast } from '../../../components/ui/Toast';
import { http } from '../../../services/http';

/** Phase 11 platform console: two-factor sign-in (ADR-052) and operational health. */
const totpKey = ['platform', 'totp'] as const;

export function TotpCard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: totpKey,
    queryFn: () => http.get<{ enabled: boolean; enabledAt: string | null }>('/auth/platform/totp'),
  });
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: totpKey });
  const start = useMutation({
    mutationFn: () => http.post<{ secret: string; otpauthUrl: string }>('/auth/platform/totp/setup'),
    onSuccess: (s) => setSetup(s),
    onError: (err) => toast.error(err),
  });
  const enable = useMutation({
    mutationFn: () =>
      http.post<{ recoveryCodes: string[] }>('/auth/platform/totp/enable', { code: code.trim() }),
    onSuccess: async (r) => {
      setRecovery(r.recoveryCodes);
      setSetup(null);
      setCode('');
      await refresh();
    },
    onError: (err) => toast.error(err),
  });
  const disable = useMutation({
    mutationFn: () => http.post<void>('/auth/platform/totp/disable', { password, code: code.trim() }),
    onSuccess: async () => {
      setPassword('');
      setCode('');
      await refresh();
      toast.success('Two-factor sign-in turned off');
    },
    onError: (err) => toast.error(err),
  });
  return (
    <Card>
      <CardHeader
        title="Two-factor sign-in"
        description="Ask for a code from an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password …) at every platform sign-in. Strongly recommended."
      />
      <CardBody className="space-y-4">
        {status.isPending ? <TableSkeleton rows={1} /> : null}
        {status.isError ? <ErrorState error={status.error} /> : null}
        {recovery ? (
          <div className="space-y-2 rounded-md border border-line bg-surface-sunken p-3" role="alert">
            <p className="text-sm font-medium">
              Save these recovery codes now. Each works once if you lose your phone; they are not shown again.
            </p>
            <ul className="grid grid-cols-2 gap-1 font-mono text-sm">
              {recovery.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {status.data?.enabled ? (
          <div className="space-y-3">
            <Badge tone="success">On</Badge>
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <TextField
                label="Current code or recovery code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <Button
              variant="secondary"
              loading={disable.isPending}
              disabled={!password || !code.trim()}
              onClick={() => disable.mutate()}
            >
              Turn off
            </Button>
          </div>
        ) : status.data ? (
          setup ? (
            <div className="space-y-3">
              <p className="text-sm">
                Add this key to your authenticator app (choose “enter a setup key”), then type the 6-digit
                code it shows.
              </p>
              <code className="block break-all rounded-md bg-surface-sunken px-3 py-2 text-sm">
                {setup.secret}
              </code>
              <a
                href={setup.otpauthUrl}
                className="text-sm text-brand-700 hover:underline dark:text-brand-300"
              >
                Open in an authenticator app on this device
              </a>
              <TextField
                label="6-digit code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <Button
                loading={enable.isPending}
                disabled={!/^\d{6}$/.test(code.trim())}
                onClick={() => enable.mutate()}
              >
                Turn on
              </Button>
            </div>
          ) : (
            <Button loading={start.isPending} onClick={() => start.mutate()}>
              Set up two-factor sign-in
            </Button>
          )
        ) : null}
      </CardBody>
    </Card>
  );
}

interface OpsHealth {
  status: 'ok' | 'degraded';
  checkedAt: string;
  outbox: { pending: number; failed: number; dead: number; oldestPendingSeconds: number | null };
  push: { retryWaiting: number; failedLastHour: number; sentLastHour: number };
  jobs: { name: string; lastRunAt: string | null; lastStatus: string | null; failuresLastHour: number }[];
  problems: string[];
}

export function OpsHealthCard() {
  const health = useQuery({
    queryKey: ['platform', 'ops-health'],
    queryFn: () => http.get<OpsHealth>('/platform/ops-health'),
    refetchInterval: 60_000,
  });
  return (
    <Card>
      <CardHeader
        title="System health"
        description="Background work: events, push sending and scheduled jobs. Refreshes every minute."
        actions={
          health.data ? (
            <Badge tone={health.data.status === 'ok' ? 'success' : 'danger'}>
              {health.data.status === 'ok' ? 'All good' : 'Needs attention'}
            </Badge>
          ) : null
        }
      />
      <CardBody className="space-y-3">
        {health.isPending ? <TableSkeleton rows={2} /> : null}
        {health.isError ? <ErrorState error={health.error} onRetry={() => void health.refetch()} /> : null}
        {health.data ? (
          <>
            {health.data.problems.length ? (
              <ul className="list-disc space-y-1 pl-5 text-sm text-danger" aria-label="Problems">
                {health.data.problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            ) : null}
            <p className="text-sm text-fg-muted">
              Events waiting {health.data.outbox.pending + health.data.outbox.failed} · dead{' '}
              {health.data.outbox.dead} · push sent last hour {health.data.push.sentLastHour} · failed{' '}
              {health.data.push.failedLastHour} · retrying {health.data.push.retryWaiting}
            </p>
            <DataTable
              rows={health.data.jobs}
              rowKey={(j) => j.name}
              empty={<p className="text-sm text-fg-subtle">No job runs recorded. Is the worker running?</p>}
              columns={[
                { header: 'Job', cell: (j) => j.name },
                {
                  header: 'Last run',
                  cell: (j) => (j.lastRunAt ? new Date(j.lastRunAt).toLocaleString() : '—'),
                },
                { header: 'Result', cell: (j) => j.lastStatus ?? '—' },
                { header: 'Failures (1 h)', cell: (j) => j.failuresLastHour, className: 'tabular-nums' },
              ]}
            />
          </>
        ) : null}
      </CardBody>
    </Card>
  );
}
